import { BadgeProps, UseType } from './Badge';

import { BadgeContextConfig } from './BadgeProvider';
import { CSSObject } from 'foundations-theming/utils';
import { BorderRadius, Color, Theme } from 'foundations-theming';
import { fwcColor } from 'foundations-theming/utils/fwc';
import { deepMerge } from '../../utils/deepMerge';

const backgroundColorMapping: Partial<Record<UseType, Color>> = {
  /**
   * Overrides
   */
  calypso: fwcColor('fwc-badge-fill-calypso'),
  'candy-apple': fwcColor('fwc-badge-fill-candy-apple'),
  koala: fwcColor('fwc-badge-fill-koala'),
  lorax: fwcColor('fwc-badge-fill-lorax'),
  marigold: fwcColor('fwc-badge-fill-marigold'),
  norman: fwcColor('fwc-badge-fill-norman'),
  oz: fwcColor('fwc-badge-fill-oz'),
  pantera: fwcColor('fwc-badge-fill-pantera'),
  sorbet: fwcColor('fwc-badge-fill-sorbet'),
  thunderdome: fwcColor('fwc-badge-fill-thunderdome'),
  /**
   * Custom
   */
  beta: fwcColor('fwc-badge-fill-beta'),
  free: fwcColor('fwc-badge-fill-free'),
  new: fwcColor('fwc-badge-fill-new'),
  blue: fwcColor('fwc-badge-fill-blue'),
  teal: fwcColor('fwc-badge-fill-teal'),
  'teal-dark': fwcColor('fwc-badge-fill-teal-dark'),
  green: fwcColor('fwc-badge-fill-green'),
  yellow: fwcColor('fwc-badge-fill-yellow'),
  orange: fwcColor('fwc-badge-fill-orange'),
  magenta: fwcColor('fwc-badge-fill-magenta'),
  purple: fwcColor('fwc-badge-fill-purple'),
  red: fwcColor('fwc-badge-fill-red'),
};

const borderColorMapping: Partial<Record<UseType, Color>> = {
  /**
   * Overrides
   */
  calypso: fwcColor('fwc-badge-border-calypso'),
  'candy-apple': fwcColor('fwc-badge-border-candy-apple'),
  koala: fwcColor('fwc-badge-border-koala'),
  lorax: fwcColor('fwc-badge-border-lorax'),
  marigold: fwcColor('fwc-badge-border-marigold'),
  norman: fwcColor('fwc-badge-border-norman'),
  oz: fwcColor('fwc-badge-border-oz'),
  pantera: fwcColor('fwc-badge-border-pantera'),
  sorbet: fwcColor('fwc-badge-border-sorbet'),
  thunderdome: fwcColor('fwc-badge-border-thunderdome'),
  /**
   * Custom
   */
  beta: fwcColor('fwc-badge-border-beta'),
  free: fwcColor('fwc-badge-border-free'),
  new: fwcColor('fwc-badge-border-new'),
  blue: fwcColor('fwc-badge-border-blue'),
  teal: fwcColor('fwc-badge-border-teal'),
  'teal-dark': fwcColor('fwc-badge-border-teal-dark'),
  green: fwcColor('fwc-badge-border-green'),
  yellow: fwcColor('fwc-badge-border-yellow'),
  orange: fwcColor('fwc-badge-border-orange'),
  magenta: fwcColor('fwc-badge-border-magenta'),
  purple: fwcColor('fwc-badge-border-purple'),
  red: fwcColor('fwc-badge-border-red'),
};

const addBorderRadiusCornerValue = ({
  theme,
  styles,
  borderRadiusKey,
  cornerRadius,
  borderRadius,
}: {
  theme: Theme;
  styles: CSSObject;
  borderRadiusKey: string;
  cornerRadius?: BorderRadius;
  borderRadius?: BorderRadius;
}) => {
  // Default to the overall borderRadius if individual values are undefined
  const value = cornerRadius ?? borderRadius;
  if (value) {
    styles[borderRadiusKey] = theme.borderRadius[value];
  }
};

export const getBackgroundColorToken = (
  use: NonNullable<BadgeProps['use']>
): Color => backgroundColorMapping[use] ?? fwcColor('fwc-badge-fill-lorax');

export const getBorderColorToken = (
  use: NonNullable<BadgeProps['use']>
): Color => borderColorMapping[use] ?? fwcColor('fwc-badge-border-free');

export const getBadgeStyleOverrides = ({
  use,
  theme,
  borderRadius,
  borderStartStartRadius,
  borderStartEndRadius,
  borderEndStartRadius,
  borderEndEndRadius,
  badgeContext,
}: {
  use: string;
  theme: Theme;
  borderRadius?: BorderRadius;
  borderStartStartRadius?: BorderRadius;
  borderStartEndRadius?: BorderRadius;
  borderEndStartRadius?: BorderRadius;
  borderEndEndRadius?: BorderRadius;
  badgeContext?: BadgeContextConfig;
}): CSSObject => {
  const styles = {};

  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderStartStartRadius',
    cornerRadius: borderStartStartRadius,
    borderRadius,
  });
  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderStartEndRadius',
    cornerRadius: borderStartEndRadius,
    borderRadius,
  });
  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderEndStartRadius',
    cornerRadius: borderEndStartRadius,
    borderRadius,
  });
  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderEndEndRadius',
    cornerRadius: borderEndEndRadius,
    borderRadius,
  });

  return deepMerge(
    {
      ...(use !== 'count' ? styles : {}),
    },
    badgeContext?.badgeStyleOverrides ?? {}
  );
};
